// @flow

import moment from 'moment';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import qq from 'fine-uploader/lib/core/s3';
import map from 'lodash/map';

import {gt} from 'app/utils/i18n';

import {MAX_FILESIZE_UPLOAD} from 'app/utils/constants';
import {getCSRFToken, getLocation} from 'app/utils/urls';

export type ACLType = 'private' | 'public-read';

export type UploaderOptionsType = {||};

export const rawUploader = (
  {
    requestEndpoint,
    accessKey,
    signatureEndpoint,
    successEndpoint,
    regionName,
    uploadCustomHeaders,
    blankIframePath,
    objACL,
    objParams,
    objKeyFunc,
    allowedExtensions = [],
  }: {
    requestEndpoint: string,
    accessKey: string,
    regionName: string,
    signatureEndpoint: string,
    successEndpoint: void | string,
    uploadCustomHeaders: {[string]: string},
    blankIframePath: string,
    objACL: ACLType,
    objParams: {[string]: string},
    objKeyFunc: (any, number) => string,
    allowedExtensions: Array<string>,
  },
  otherOptions: UploaderOptionsType
) => {
  ensureWontPrefix(objParams);

  return new FineUploaderS3({
    options: {
      request: {
        params: objParams,
        endpoint: requestEndpoint,
        accessKey,
      },
      signature: {
        endpoint: signatureEndpoint,
        customHeaders: uploadCustomHeaders,
        version: 4,
      },
      uploadSuccess: {
        endpoint: successEndpoint,
        customHeaders: uploadCustomHeaders,
      },
      iframeSupport: {
        localBlankPagePath: blankIframePath,
      },
      objectProperties: {
        region: regionName,
        acl: objACL,
        key: function (fileId) {
          return objKeyFunc(this, fileId);
        },
      },
      chunking: {enabled: true},
      validation: {
        sizeLimit: MAX_FILESIZE_UPLOAD.bytes,
        allowedExtensions: allowedExtensions,
      },
      maxConnections: 3,
      failedUploadTextDisplay: {
        mode: 'custom',
        responseProperty: 'error',
        enableTooltip: true,
      },
      messages: {
        emptyError: 'EMPTY_CONTENT',
        typeError: 'NOT_ALLOWED_EXTENSION',
        sizeError: 'FILE_TOO_LARGE',
      },
      ...otherOptions,
    },
  });
};

export const getMediaPath = (mediaURL: string) =>
  getLocation(mediaURL).pathname.replace(/^\//, '');

export const removeMediaPath = (fileURL: string, mediaURL: string) => {
  const mediaPath = getMediaPath(mediaURL);
  return fileURL.replace(mediaPath, '');
};

export const generateUUID = () => qq.getUniqueId();

export const generateS3Key = (
  filename: string,
  mediaURL: string,
  uploadTo: string,
  accountId: number
): string => {
  const mediaPath = getMediaPath(mediaURL);
  var extension = qq.getExtension(filename);
  if (extension != null) {
    extension = `.${extension}`;
  } else {
    extension = '';
  }
  const date = moment().format('YY\\sMM');
  const uuid = generateUUID();
  return `${mediaPath}${uploadTo}/${accountId}/${date}/${uuid}${extension}`;
};

export const preparedUploader = (
  {
    requestEndpoint,
    accessKey,
    signatureEndpoint,
    successEndpoint,
    blankIframePath,
    objACL,
    objKeyFunc,
    objParams,
    regionName,
    uploadCustomHeaders = {},
    allowedExtensions = [],
  }: {
    requestEndpoint: string,
    accessKey: string,
    regionName: string,
    signatureEndpoint: string,
    successEndpoint: void | string,
    blankIframePath: string,
    objACL: ACLType,
    objParams: {[string]: string},
    uploadCustomHeaders?: {[string]: string},
    objKeyFunc?: (any, number) => string,
    allowedExtensions?: Array<string>,
  },
  accountId: number,
  mediaURL: string,
  otherOptions: UploaderOptionsType = Object.freeze({})
) => {
  const genericObjKeyFunc = (uploader, fileId) => {
    return generateS3Key(
      uploader.getName(fileId),
      mediaURL,
      'files',
      accountId
    );
  };

  return rawUploader(
    {
      requestEndpoint,
      accessKey,
      signatureEndpoint,
      successEndpoint,
      uploadCustomHeaders: {
        ...uploadCustomHeaders,
        'X-CSRFToken': getCSRFToken(),
      },
      blankIframePath,
      objACL,
      objParams,
      objKeyFunc: objKeyFunc || genericObjKeyFunc,
      regionName,
      allowedExtensions,
    },
    otherOptions
  );
};

export const preparedImageUploader = (
  opts: {[string]: any},
  accountId: number,
  mediaURL: string,
  otherOptions: UploaderOptionsType = Object.freeze({})
) =>
  preparedUploader(
    {
      ...opts,
      allowedExtensions: [
        'gif',
        'jpg',
        'jpeg',
        'bmp',
        'png',
        'ico',
        'tiff',
        'tif',
        'eps',
      ],
    },
    accountId,
    mediaURL,
    otherOptions
  );

export const preparedDocsUploader = (
  opts: {[string]: any},
  accountId: number,
  mediaURL: string,
  otherOptions: UploaderOptionsType = Object.freeze({})
) =>
  preparedUploader(
    {
      ...opts,
      allowedExtensions: ['docx'],
      objACL: 'private',
      objKeyFunc: (uploader, fileId) => {
        return generateS3Key(
          uploader.getName(fileId),
          mediaURL,
          'docimport',
          accountId
        );
      },
      successEndpoint: undefined,
    },
    accountId,
    mediaURL,
    otherOptions
  );

export const getErrorMessage = (uploader: any, code: string) => {
  const extensions = uploader.options.validation.allowedExtensions.join(', ');
  const DEFAULT_MESSAGE = gt(
    'There was an error uploading the file. Please try again later'
  );

  const messages = {
    EMPTY_CONTENT: gt('This file is empty, please upload a different file'),
    NOT_ALLOWED_EXTENSION: gt(
      'You may only upload these files extensions here: %s',
      extensions
    ),
    FILE_TOO_LARGE: gt(
      'File is too large. The maximum file size allowed is %s',
      MAX_FILESIZE_UPLOAD.humanReadable
    ),
  };

  return messages[code] || DEFAULT_MESSAGE;
};

const fineUploadPrefixOptsToCheck = [
  'UNPREFIXED_PARAM_NAMES',
  'CASE_SENSITIVE_PARAM_NAMES',
];

const ensureWontPrefix = (params: {[string]: any}) =>
  map(params, (value, key) =>
    map(fineUploadPrefixOptsToCheck, (opt) => {
      let optValue = qq.s3.util[opt];
      if (optValue.indexOf(key) === -1) {
        optValue.push(key);
      }
    })
  );
